/**
 * Direct Line Message Type Taxonomy
 *
 * These constants define the message type taxonomy used for communication between
 * the frontend and core agent. They ensure consistent handling of different message
 * types across the DirectLine channel.
 *
 * Message types are sent in activity.channelData.message_type to distinguish between
 * chat responses, results payloads, and control messages.
 */

// Chat response message types
export const MESSAGE_TYPE_CHAT_RESPONSE = "chat_response";
export const MESSAGE_TYPE_CHAT_RESPONSE_CHUNK = "chat_response_chunk";

// Results payload for deal/document data
export const MESSAGE_TYPE_RESULTS_PAYLOAD = "results_payload";
export const MESSAGE_TYPE_RESULTS_PAYLOAD_CHUNK = "results_payload_chunk";

// Facts streaming (sent individually after results/docs to avoid size limits)
export const MESSAGE_TYPE_FACTS_STREAM_ITEM = "facts_stream_item";

// Bootstrap lifecycle messages
export const MESSAGE_TYPE_BOOTSTRAP_ACK = "bootstrap_ack";
export const MESSAGE_TYPE_BOOTSTRAP_ERROR = "bootstrap_error";

// Workflow status messages for action "thinking" indicators
export const MESSAGE_TYPE_WORKFLOW_STATUS = "workflow_status";

/**
 * channelData Schema Definitions
 *
 * RESULTS_PAYLOAD:
 * {
 *   message_type: "results_payload",
 *   turnId: string,              // Turn identifier (top-level in channelData)
 *   payload: {
 *     results: Deal[],           // Array of deal objects
 *     documents: Document[],     // Array of document objects
 *     facts: Fact[],             // Array of fact objects (merged, not replaced, by ResultsSlice)
 *     projectId: string          // Project identifier
 *   }
 * }
 *
 * RESULTS_PAYLOAD_CHUNK:
 * {
 *   message_type: "results_payload_chunk",
 *   chunkId: string,            // UUID identifying the complete set of chunks
 *   chunkIndex: number,         // 0-based index of this chunk
 *   chunkCount: number,         // Total number of chunks in the set
 *   sizeBytes: number,          // Size of this chunk in bytes (diagnostic)
 *   turnId: string,             // Turn identifier
 *   projectId: string,          // Project identifier
 *   payload: {
 *     results: Deal[],          // Slice of deal objects for this chunk
 *     documents: Document[],    // Documents for deals in this chunk
 *     facts: Fact[],            // Always empty - facts streamed separately
 *     turnId: string,           // Turn identifier (matches envelope)
 *     projectId: string         // Project identifier (matches envelope)
 *   }
 * }
 *
 * FACTS_STREAM_ITEM (sent individually after results/docs):
 * {
 *   message_type: "facts_stream_item",
 *   turnId: string,             // Turn identifier
 *   projectId: string,          // Project identifier
 *   factIndex: number,          // 0-based index of this fact
 *   factCount: number,          // Total number of facts being streamed
 *   fact: Fact                  // The fact object
 * }
 *
 * CHAT_RESPONSE:
 * {
 *   message_type: "chat_response",
 *   turnId: string,             // Turn identifier
 *   projectId: string,          // Project identifier
 *   streamStatus: "complete",   // Stream status indicator
 *   sequence?: number           // Optional sequence number for ordering
 * }
 *
 * CHAT_RESPONSE_CHUNK (for streaming):
 * {
 *   message_type: "chat_response_chunk",
 *   turnId: string,             // Turn identifier (consistent across chunks)
 *   projectId: string,          // Project identifier
 *   streamStatus: "partial",    // Indicates partial stream
 *   sequence: number            // Sequence number for ordering chunks
 * }
 *
 * BOOTSTRAP_ACK:
 * {
 *   message_type: "bootstrap_ack",
 *   payload: {
 *     projectId: string,
 *     conversationId: string,
 *     username: string,
 *     projectLabel: string,
 *     name?: string,            // Resolved project name (for rehydration)
 *     summary?: string          // Resolved project summary (for rehydration)
 *   }
 * }
 *
 * BOOTSTRAP_ERROR:
 * {
 *   message_type: "bootstrap_error",
 *   error: string               // Error message
 * }
 *
 * WORKFLOW_STATUS:
 * {
 *   message_type: "workflow_status",
 *   turnId: string,             // Turn identifier
 *   projectId: string,          // Project identifier
 *   status: {
 *     action: string,           // Action name (e.g., "create_filter", "search_deals")
 *     state: "start" | "complete", // Action state
 *     label: string,            // Human-readable status message from config
 *     todoPlan?: {              // Optional turn plan snapshot (included with every status update)
 *       archetype: string,      // Plan archetype (e.g., "deal_search_only")
 *       steps: Array<{
 *         index: number,        // Step index (0-based)
 *         friendlyName: string, // Human-readable step name for UI display
 *         actionName: string,   // Internal action name
 *         rationale: string,    // Why this step is needed
 *         status: "pending" | "in_progress" | "complete" // Step execution status
 *       }>
 *     },
 *     scopeSummary?: {          // Optional scope summary for debugging
 *       scopedEntityCount: number
 *     }
 *   }
 * }
 */
